/**
 * Date Helpers — South African date formatting and utilities.
 *
 * All dates in Inbox are displayed in DD/MM/YYYY format
 * per South African convention.
 */

/**
 * Format a date as DD/MM/YYYY.
 */
export function formatDate(date) {
  if (!date) return '—';
  const d = new Date(date);
  return d.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Format a date with time as DD/MM/YYYY HH:MM.
 */
export function formatDateTime(date) {
  if (!date) return '—';
  const d = new Date(date);
  return d.toLocaleString('en-ZA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a date as a long-form "D Month YYYY" string in the browser's
 * LOCAL timezone (en-ZA locale). Example: "9 July 2026".
 *
 * NOTE: this mirrors an inline expression that was previously duplicated
 * across several email/quote builders — it has NO null/NaN guard on
 * purpose, so its output is byte-identical to the code it replaced
 * (`new Date(value).toLocaleDateString('en-ZA', {...})`). Callers that
 * may pass empty values guard before calling.
 */
export function formatLongDate(value) {
  return new Date(value).toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Format a date as DD/MM/YYYY (SA house style) in the browser's LOCAL
 * timezone. Example: "09/07/2026". Built explicitly from the date's local
 * parts so the field order is pinned regardless of environment / ICU
 * version — the en-ZA locale default is year-first (2026/07/09), which is
 * NOT the house style, so we do not rely on it.
 *
 * No null guard by design — mirrors the inline expressions it replaced, so
 * callers keep their own guards. Invalid dates return "Invalid Date"
 * (byte-identical to the previous toLocaleDateString behaviour).
 */
export function formatDateZA(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return d.toLocaleDateString('en-ZA');
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/**
 * Format a date+time as "DD/MM/YYYY, HH:MM:SS" (SA house-style date + the
 * locale's 24-hour time) in the browser's LOCAL timezone. Example:
 * "09/07/2026, 14:30:00". The date is built explicitly (see formatDateZA);
 * the time portion and ", " separator come from the locale exactly as
 * before, so ONLY the date field order changed — the time and separator
 * stay byte-identical to the previous toLocaleString output.
 *
 * No null guard by design. Invalid dates return "Invalid Date".
 */
export function formatDateTimeZA(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return d.toLocaleString('en-ZA');
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const time = d.toLocaleTimeString('en-ZA');
  return `${dd}/${mm}/${d.getFullYear()}, ${time}`;
}

/**
 * Format a date+time as "DD/MM/YYYY HH:mm" in Africa/Johannesburg (SAST),
 * independent of the browser's timezone. Example: "22/09/2026 14:30".
 * Returns '' for missing / unparseable values.
 *
 * Moved here verbatim from the handover memo generator so any surface that
 * needs a timezone-pinned timestamp (rather than the LOCAL-timezone
 * formatDateTime / formatDateTimeZA) shares one implementation.
 */
export function formatDateTimeSAST(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Johannesburg',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t) => (parts.find((x) => x.type === t) || {}).value || '';
  return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
}

/**
 * Format a date for input fields (YYYY-MM-DD).
 */
export function formatDateForInput(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
}

/**
 * Get a time-aware greeting string.
 */
export function getGreeting(name) {
  const hour = new Date().getHours();
  if (hour < 12) return `Good morning, ${name}`;
  if (hour < 17) return `Good afternoon, ${name}`;
  return `Good evening, ${name}`;
}

/**
 * Get the current month-year string in "YYYY-MM" format.
 */
export function getCurrentMonthYear() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Format a month-year string ("2026-04") as a display string ("April 2026").
 */
export function formatMonthYearDisplay(monthYear) {
  if (!monthYear) return '—';
  const [year, month] = monthYear.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
}

/**
 * Calculate the difference between two dates in human-readable form.
 */
export function timeAgo(date) {
  if (!date) return '—';
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = now - then;

  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

/**
 * Returns the calendar date in Africa/Johannesburg as a YYYY-MM-DD string —
 * today by default, or the SAST date of `now` when a Date is passed (the
 * referral utilities pass one so their derived state is testable at a fixed
 * instant). Used to gate the intro video to once per SAST calendar day,
 * regardless of the user's device or browser timezone.
 *
 * Output shape: 'YYYY-MM-DD', zero-padded, e.g. '2026-08-01'. Throws a
 * RangeError for an invalid Date, as Intl.DateTimeFormat does.
 */
export function getTodaySASTDateString(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const y = parts.find((p) => p.type === 'year').value;
  const m = parts.find((p) => p.type === 'month').value;
  const d = parts.find((p) => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}

/**
 * SAST calendar month key (YYYY-MM) for a Date or ISO string. Returns
 * null for missing / unparseable values. Example: "2026-07".
 *
 * Shared home for the month-key logic (moved here from dashboardStats.js
 * so all date derivation lives in one module). Note this is the SAST
 * (Africa/Johannesburg) month key — it can differ near month boundaries
 * from the LOCAL-timezone month keys produced by getCurrentMonthYear() /
 * filesApi.formatMonthYear(); the two are intentionally NOT unified.
 */
export function getMonthKeySAST(value) {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year').value;
  const m = parts.find((p) => p.type === 'month').value;
  return `${y}-${m}`;
}

/**
 * Returns a Date representing the first millisecond of the current calendar
 * month in SAST (Africa/Johannesburg, UTC+2). Used to filter the "completed"
 * stage in the pipeline: only files completed this month are shown.
 */
export function getStartOfCurrentMonthSAST() {
  const now = new Date();
  // Parse today in SAST to get the correct year/month even near midnight UTC
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(now);
  const year  = parseInt(parts.find(p => p.type === 'year').value, 10);
  const month = parseInt(parts.find(p => p.type === 'month').value, 10) - 1; // 0-indexed
  // Midnight SAST = UTC − 2 h  (SAST is UTC+2, so midnight SAST is 22:00 UTC the previous day)
  return new Date(Date.UTC(year, month, 1) - 2 * 60 * 60 * 1000);
}

/**
 * Format a millisecond duration as a tight "Xd / Xh / Xm" indicator.
 * Used in the File Completed column to show how long a file took from
 * submission to completion.
 *   ms >= 1 day  → "3.2d"   (one decimal place)
 *   ms >= 1 hour → "5h"     (rounded)
 *   else         → "45m"    (rounded)
 * Returns "—" for null / undefined / NaN / negative values so the slot
 * never renders garbage.
 */
export function formatTotalDuration(ms) {
  if (ms === null || ms === undefined || Number.isNaN(ms) || ms < 0) return '—';
  const days = ms / 86_400_000;
  if (days >= 1) return `${days.toFixed(1)}d`;
  const hours = ms / 3_600_000;
  if (hours >= 1) return `${Math.round(hours)}h`;
  const minutes = ms / 60_000;
  return `${Math.round(minutes)}m`;
}
